import React, { useState } from "react";
import {
  Scale,
  FileText,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  User,
  Gavel,
  Shield,
  Upload,
  Send,
  Archive,
  BookOpen,
  Users,
  MessageSquare,
  Bell,
  Target,
  TrendingUp,
  BarChart3,
  FileCheck,
  Lock,
  Globe,
  Building,
  Phone,
  Mail,
  MapPin,
  ExternalLink,
  Copy,
  Share2,
  Printer,
  History,
  Star,
  Flag,
  Zap,
} from "lucide-react";
import { useData } from "../context/DataContext";
import { Case } from "../context/DataContext";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Table from "../components/ui/Table";
import Modal from "../components/ui/Modal";
import Badge from "../components/ui/Badge";
import { format } from "date-fns";

export default function Legal() {
  const { state, dispatch } = useData();
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [showCaseModal, setShowCaseModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "cases" | "documents" | "deadlines" | "workflow" | "analytics"
  >("cases");
  const [searchTerm, setSearchTerm] = useState("");

  const handleAddCase = (caseData: Omit<Case, "id">) => {
    const newCase: Case = {
      ...caseData,
      id: Date.now().toString(),
    };
    dispatch({ type: "ADD_CASE", payload: newCase });
    setShowAddModal(false);
  };

  const handleUpdateCase = (updatedCase: Case) => {
    dispatch({ type: "UPDATE_CASE", payload: updatedCase });
    setShowCaseModal(false);
  };

  // Sample data for new features
  const legalAdvisors = [
    {
      id: 1,
      name: "Dr. Ahmed Hassan",
      specialization: "Labor Law",
      status: "available",
      cases: 12,
      rating: 4.8,
    },
    {
      id: 2,
      name: "Ms. Fatma Ibrahim",
      specialization: "Corporate Law",
      status: "busy",
      cases: 8,
      rating: 4.9,
    },
    {
      id: 3,
      name: "Mr. Omar Ali",
      specialization: "Regulatory Compliance",
      status: "available",
      cases: 15,
      rating: 4.7,
    },
  ];

  const workflowSteps = [
    {
      id: 1,
      name: "Case Intake",
      status: "completed",
      assignedTo: "Legal Secretary",
      dueDate: "2024-01-10",
    },
    {
      id: 2,
      name: "Initial Review",
      status: "in-progress",
      assignedTo: "Dr. Ahmed Hassan",
      dueDate: "2024-01-15",
    },
    {
      id: 3,
      name: "Document Collection",
      status: "pending",
      assignedTo: "Legal Assistant",
      dueDate: "2024-01-20",
    },
    {
      id: 4,
      name: "Legal Analysis",
      status: "pending",
      assignedTo: "Ms. Fatma Ibrahim",
      dueDate: "2024-01-25",
    },
    {
      id: 5,
      name: "Resolution Strategy",
      status: "pending",
      assignedTo: "Legal Team",
      dueDate: "2024-01-30",
    },
  ];

  const caseAnalytics = {
    totalCases: 45,
    resolvedThisMonth: 8,
    averageResolutionTime: "23 days",
    successRate: "87%",
    caseTypes: [
      { type: "Labor Disputes", count: 18, percentage: 40 },
      { type: "Regulatory Issues", count: 12, percentage: 27 },
      { type: "Contract Disputes", count: 10, percentage: 22 },
      { type: "Other", count: 5, percentage: 11 },
    ],
  };

  const handleCreateDocument = (documentData: any) => {
    // Simulate document creation
    // Creating document
    setShowDocumentModal(false);
  };

  const handleAssignWorkflow = (workflowData: any) => {
    // Simulate workflow assignment
    // Assigning workflow
    setShowWorkflowModal(false);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "open":
        return <Badge variant="error">Open</Badge>;
      case "in-progress":
        return <Badge variant="warning">In Progress</Badge>;
      case "resolved":
        return <Badge variant="success">Resolved</Badge>;
      case "closed":
        return <Badge variant="default">Closed</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "high":
        return <Badge variant="error">High</Badge>;
      case "medium":
        return <Badge variant="warning">Medium</Badge>;
      case "low":
        return <Badge variant="success">Low</Badge>;
      default:
        return <Badge variant="default">{priority}</Badge>;
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case "dispute":
        return <Badge variant="warning">Dispute</Badge>;
      case "labor":
        return <Badge variant="error">Labor</Badge>;
      case "regulatory":
        return <Badge variant="info">Regulatory</Badge>;
      default:
        return <Badge variant="default">{type}</Badge>;
    }
  };

  const caseColumns = [
    {
      key: "title",
      header: "Case",
      render: (case_: Case) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{case_.title}</div>
          <div className="text-sm text-gray-500">#{case_.id}</div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (case_: Case) => getTypeBadge(case_.type),
    },
    {
      key: "assignedTo",
      header: "Assigned To",
      render: (case_: Case) => case_.assignedTo,
    },
    {
      key: "priority",
      header: "Priority",
      render: (case_: Case) => getPriorityBadge(case_.priority),
    },
    {
      key: "status",
      header: "Status",
      render: (case_: Case) => getStatusBadge(case_.status),
    },
    {
      key: "createdDate",
      header: "Created",
      render: (case_: Case) =>
        format(new Date(case_.createdDate), "MMM dd, yyyy"),
    },
    {
      key: "dueDate",
      header: "Due Date",
      render: (case_: Case) =>
        case_.dueDate ? format(new Date(case_.dueDate), "MMM dd, yyyy") : "-",
    },
    {
      key: "actions",
      header: "Actions",
      render: (case_: Case) => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedCase(case_);
              setShowCaseModal(true);
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedCase(case_);
              setShowCaseModal(true);
            }}
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const legalDocuments = [
    {
      id: "1",
      title: "Employment Contract Template",
      type: "Contract",
      category: "Labor",
      lastModified: "2024-01-15",
      size: "1.2 MB",
    },
    {
      id: "2",
      title: "Pilot License Agreement",
      type: "Agreement",
      category: "Regulatory",
      lastModified: "2024-01-10",
      size: "856 KB",
    },
    {
      id: "3",
      title: "Dispute Resolution Policy",
      type: "Policy",
      category: "Dispute",
      lastModified: "2024-01-05",
      size: "2.1 MB",
    },
  ];

  const upcomingDeadlines = [
    {
      id: "1",
      title: "Court Hearing - Employment Dispute",
      caseId: "CASE-001",
      date: "2024-02-15",
      type: "Hearing",
      priority: "high",
    },
    {
      id: "2",
      title: "Contract Renewal Deadline",
      caseId: "CONTRACT-002",
      date: "2024-02-20",
      type: "Contract",
      priority: "medium",
    },
    {
      id: "3",
      title: "Regulatory Compliance Review",
      caseId: "REG-003",
      date: "2024-02-25",
      type: "Compliance",
      priority: "high",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Legal Affairs</h1>
          <p className="text-gray-600">
            Case management, document archiving, and legal workflow
          </p>
        </div>
        <div className="flex gap-2">
          {activeTab === "cases" && (
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Case
            </Button>
          )}
          {activeTab === "documents" && (
            <Button onClick={() => setShowDocumentModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Upload Document
            </Button>
          )}
          {activeTab === "workflow" && (
            <Button onClick={() => setShowWorkflowModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Assign Workflow
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Scale className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Cases</p>
              <p className="text-2xl font-bold text-gray-900">
                {state.cases.length}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Clock className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Open Cases</p>
              <p className="text-2xl font-bold text-gray-900">
                {
                  state.cases.filter(
                    (c) => c.status === "open" || c.status === "in-progress"
                  ).length
                }
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Resolved</p>
              <p className="text-2xl font-bold text-gray-900">
                {state.cases.filter((c) => c.status === "resolved").length}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Urgent</p>
              <p className="text-2xl font-bold text-gray-900">
                {state.cases.filter((c) => c.priority === "high").length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "cases", name: "Cases", icon: Scale },
            { id: "documents", name: "Documents", icon: FileText },
            { id: "deadlines", name: "Deadlines", icon: Calendar },
            { id: "workflow", name: "Workflow", icon: Target },
            { id: "analytics", name: "Analytics", icon: BarChart3 },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "cases" && (
        <div className="space-y-6">
          {/* Search and Filters */}
          <Card>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search cases..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <select className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option>All Types</option>
                  <option>Dispute</option>
                  <option>Labor</option>
                  <option>Regulatory</option>
                </select>
                <select className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option>All Status</option>
                  <option>Open</option>
                  <option>In Progress</option>
                  <option>Resolved</option>
                </select>
                <Button variant="outline">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
                <Button variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </Card>

          {/* Cases Table */}
          <Card padding="none">
            <Table
              data={state.cases}
              columns={caseColumns}
              onRowClick={(case_) => {
                setSelectedCase(case_);
                setShowCaseModal(true);
              }}
              emptyMessage="No cases found"
            />
          </Card>
        </div>
      )}

      {activeTab === "documents" && (
        <Card padding="none">
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Document
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Modified
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {legalDocuments.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 text-gray-400 mr-3" />
                        <div className="text-sm font-medium text-gray-900">
                          {doc.title}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant="info">{doc.type}</Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant="info">{doc.category}</Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {format(new Date(doc.lastModified), "MMM dd, yyyy")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {doc.size}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {activeTab === "deadlines" && (
        <div className="space-y-4">
          {upcomingDeadlines.map((deadline) => (
            <Card key={deadline.id}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {deadline.title}
                  </h3>
                  <p className="text-gray-600 mt-1">
                    Case ID: {deadline.caseId}
                  </p>
                  <div className="flex items-center mt-3 space-x-4">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                      <span className="text-sm text-gray-600">
                        {format(new Date(deadline.date), "EEEE, MMMM dd, yyyy")}
                      </span>
                    </div>
                    <Badge variant="info">{deadline.type}</Badge>
                  </div>
                </div>
                <div className="ml-4">
                  {deadline.priority === "high" ? (
                    <Badge variant="error">Urgent</Badge>
                  ) : (
                    <Badge variant="warning">Medium</Badge>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {activeTab === "workflow" && (
        <div className="space-y-6">
          {/* Legal Advisors */}
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Legal Advisors
              </h3>
              <Button variant="outline">
                <Users className="h-4 w-4 mr-2" />
                Manage Team
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {legalAdvisors.map((advisor) => (
                <div
                  key={advisor.id}
                  className="p-4 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">
                      {advisor.name}
                    </h4>
                    <Badge
                      variant={
                        advisor.status === "available" ? "success" : "warning"
                      }
                    >
                      {advisor.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    {advisor.specialization}
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{advisor.cases} cases</span>
                    <div className="flex items-center">
                      <Star className="h-4 w-4 text-yellow-400 mr-1" />
                      <span className="text-gray-700">{advisor.rating}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Workflow Steps */}
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Case Workflow
              </h3>
              <Button variant="outline">
                <Target className="h-4 w-4 mr-2" />
                Customize Workflow
              </Button>
            </div>
            <div className="space-y-4">
              {workflowSteps.map((step, index) => (
                <div key={step.id} className="flex items-center space-x-4">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step.status === "completed"
                        ? "bg-green-100 text-green-600"
                        : step.status === "in-progress"
                        ? "bg-blue-100 text-blue-600"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {step.status === "completed" ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">{step.name}</h4>
                      <Badge
                        variant={
                          step.status === "completed"
                            ? "success"
                            : step.status === "in-progress"
                            ? "info"
                            : "default"
                        }
                      >
                        {step.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">
                      Assigned to: {step.assignedTo}
                    </p>
                    <p className="text-sm text-gray-500">
                      Due: {format(new Date(step.dueDate), "MMM dd, yyyy")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {activeTab === "analytics" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Case Analytics Overview */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Case Analytics
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">
                    {caseAnalytics.totalCases}
                  </p>
                  <p className="text-sm text-gray-600">Total Cases</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">
                    {caseAnalytics.resolvedThisMonth}
                  </p>
                  <p className="text-sm text-gray-600">Resolved This Month</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <p className="text-2xl font-bold text-orange-600">
                    {caseAnalytics.averageResolutionTime}
                  </p>
                  <p className="text-sm text-gray-600">Avg Resolution Time</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">
                    {caseAnalytics.successRate}
                  </p>
                  <p className="text-sm text-gray-600">Success Rate</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Case Types Distribution */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Case Types Distribution
            </h3>
            <div className="space-y-3">
              {caseAnalytics.caseTypes.map((type) => (
                <div
                  key={type.type}
                  className="flex items-center justify-between"
                >
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900">
                        {type.type}
                      </span>
                      <span className="text-sm text-gray-600">
                        {type.count} cases
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${type.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Recent Activity */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Recent Activity
            </h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">
                    Case #CASE-001 resolved
                  </p>
                  <p className="text-xs text-gray-500">2 hours ago</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">
                    New case assigned to Dr. Ahmed Hassan
                  </p>
                  <p className="text-xs text-gray-500">4 hours ago</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">
                    Document uploaded for CASE-003
                  </p>
                  <p className="text-xs text-gray-500">6 hours ago</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Performance Metrics */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Performance Metrics
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Cases Resolved This Week
                </span>
                <span className="font-medium text-gray-900">5</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Average Response Time
                </span>
                <span className="font-medium text-gray-900">2.3 days</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Client Satisfaction
                </span>
                <span className="font-medium text-gray-900">4.7/5</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Document Processing Time
                </span>
                <span className="font-medium text-gray-900">1.5 days</span>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Case Details Modal */}
      {selectedCase && (
        <CaseDetailsModal
          case_={selectedCase}
          isOpen={showCaseModal}
          onClose={() => {
            setShowCaseModal(false);
            setSelectedCase(null);
          }}
          onUpdate={handleUpdateCase}
        />
      )}

      {/* Add Case Modal */}
      <AddCaseModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddCase}
      />

      {/* Document Modal */}
      <DocumentModal
        isOpen={showDocumentModal}
        onClose={() => setShowDocumentModal(false)}
        onCreate={handleCreateDocument}
      />

      {/* Workflow Modal */}
      <WorkflowModal
        isOpen={showWorkflowModal}
        onClose={() => setShowWorkflowModal(false)}
        onAssign={handleAssignWorkflow}
      />
    </div>
  );
}

// Case Details Modal Component
function CaseDetailsModal({
  case_,
  isOpen,
  onClose,
  onUpdate,
}: {
  case_: Case;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (case_: Case) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(case_);

  const handleSave = () => {
    onUpdate(formData);
    setIsEditing(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Case Details" size="lg">
      <div className="space-y-6">
        {/* Case Header */}
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-xl font-bold text-gray-900">{case_.title}</h3>
            <div className="flex items-center mt-2 space-x-4">
              <Badge
                variant={
                  case_.type === "dispute"
                    ? "warning"
                    : case_.type === "labor"
                    ? "error"
                    : "info"
                }
              >
                {case_.type}
              </Badge>
              <Badge
                variant={
                  case_.status === "open"
                    ? "error"
                    : case_.status === "in-progress"
                    ? "warning"
                    : "success"
                }
              >
                {case_.status}
              </Badge>
              <Badge
                variant={
                  case_.priority === "high"
                    ? "error"
                    : case_.priority === "medium"
                    ? "warning"
                    : "success"
                }
              >
                {case_.priority} Priority
              </Badge>
            </div>
          </div>
        </div>

        {/* Case Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">Case Information</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Assigned To
                </label>
                <p className="text-sm text-gray-900">{case_.assignedTo}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Created Date
                </label>
                <p className="text-sm text-gray-900">
                  {format(new Date(case_.createdDate), "MMM dd, yyyy")}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Due Date
                </label>
                <p className="text-sm text-gray-900">
                  {case_.dueDate
                    ? format(new Date(case_.dueDate), "MMM dd, yyyy")
                    : "Not set"}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">Description</h4>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">{case_.description}</p>
            </div>
          </div>
        </div>

        {/* Documents */}
        {case_.documents && case_.documents.length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">
              Related Documents
            </h4>
            <div className="space-y-2">
              {case_.documents.map((doc, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">{doc}</span>
                  </div>
                  <Button variant="ghost" size="sm">
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => setIsEditing(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Case
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Add Case Modal Component
function AddCaseModal({
  isOpen,
  onClose,
  onAdd,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (case_: Omit<Case, "id">) => void;
}) {
  const [formData, setFormData] = useState({
    title: "",
    type: "dispute" as const,
    status: "open" as const,
    assignedTo: "",
    priority: "medium" as const,
    createdDate: new Date().toISOString().split("T")[0],
    dueDate: "",
    description: "",
    documents: [] as string[],
    caseNumber: "",
    parties: [] as string[],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData);
    setFormData({
      title: "",
      type: "dispute",
      status: "open",
      assignedTo: "",
      priority: "medium",
      createdDate: new Date().toISOString().split("T")[0],
      dueDate: "",
      description: "",
      documents: [],
      caseNumber: "",
      parties: [],
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Case" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Case Title"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Case Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value as any })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="dispute">Dispute</option>
              <option value="labor">Labor</option>
              <option value="regulatory">Regulatory</option>
            </select>
          </div>
          <Input
            label="Assigned To"
            value={formData.assignedTo}
            onChange={(e) =>
              setFormData({ ...formData, assignedTo: e.target.value })
            }
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={formData.priority}
              onChange={(e) =>
                setFormData({ ...formData, priority: e.target.value as any })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <Input
            label="Created Date"
            type="date"
            value={formData.createdDate}
            onChange={(e) =>
              setFormData({ ...formData, createdDate: e.target.value })
            }
            required
          />
          <Input
            label="Due Date (Optional)"
            type="date"
            value={formData.dueDate}
            onChange={(e) =>
              setFormData({ ...formData, dueDate: e.target.value })
            }
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Add Case</Button>
        </div>
      </form>
    </Modal>
  );
}

// Document Modal Component
function DocumentModal({
  isOpen,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (document: any) => void;
}) {
  const [formData, setFormData] = useState({
    title: "",
    type: "contract",
    category: "labor",
    description: "",
    caseId: "",
    confidentiality: "internal",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate({
      ...formData,
      lastModified: new Date().toISOString().split("T")[0],
      size: "1.2 MB",
    });
    setFormData({
      title: "",
      type: "contract",
      category: "labor",
      description: "",
      caseId: "",
      confidentiality: "internal",
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Upload Legal Document"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Document Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Document Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="contract">Contract</option>
              <option value="agreement">Agreement</option>
              <option value="policy">Policy</option>
              <option value="regulation">Regulation</option>
              <option value="correspondence">Correspondence</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) =>
                setFormData({ ...formData, category: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="labor">Labor</option>
              <option value="regulatory">Regulatory</option>
              <option value="dispute">Dispute</option>
              <option value="corporate">Corporate</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Related Case (Optional)
          </label>
          <select
            value={formData.caseId}
            onChange={(e) =>
              setFormData({ ...formData, caseId: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select Case</option>
            <option value="CASE-001">CASE-001 - Employment Dispute</option>
            <option value="CASE-002">CASE-002 - Contract Review</option>
            <option value="CASE-003">CASE-003 - Regulatory Compliance</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Confidentiality Level
          </label>
          <select
            value={formData.confidentiality}
            onChange={(e) =>
              setFormData({ ...formData, confidentiality: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="public">Public</option>
            <option value="internal">Internal</option>
            <option value="confidential">Confidential</option>
            <option value="restricted">Restricted</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">
            Click to upload or drag and drop
          </p>
          <p className="text-xs text-gray-500">PDF, DOC, DOCX up to 10MB</p>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Upload Document</Button>
        </div>
      </form>
    </Modal>
  );
}

// Workflow Modal Component
function WorkflowModal({
  isOpen,
  onClose,
  onAssign,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (workflow: any) => void;
}) {
  const [formData, setFormData] = useState({
    caseId: "",
    workflowType: "standard",
    assignedTo: "",
    priority: "medium",
    dueDate: "",
    notes: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAssign({
      ...formData,
      dueDate: new Date(formData.dueDate).toISOString().split("T")[0],
    });
    setFormData({
      caseId: "",
      workflowType: "standard",
      assignedTo: "",
      priority: "medium",
      dueDate: "",
      notes: "",
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Assign Workflow" size="md">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Case
          </label>
          <select
            value={formData.caseId}
            onChange={(e) =>
              setFormData({ ...formData, caseId: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          >
            <option value="">Select Case</option>
            <option value="CASE-001">CASE-001 - Employment Dispute</option>
            <option value="CASE-002">CASE-002 - Contract Review</option>
            <option value="CASE-003">CASE-003 - Regulatory Compliance</option>
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Workflow Type
            </label>
            <select
              value={formData.workflowType}
              onChange={(e) =>
                setFormData({ ...formData, workflowType: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="standard">Standard</option>
              <option value="expedited">Expedited</option>
              <option value="complex">Complex</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={formData.priority}
              onChange={(e) =>
                setFormData({ ...formData, priority: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Assign To
          </label>
          <select
            value={formData.assignedTo}
            onChange={(e) =>
              setFormData({ ...formData, assignedTo: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          >
            <option value="">Select Legal Advisor</option>
            <option value="Dr. Ahmed Hassan">
              Dr. Ahmed Hassan - Labor Law
            </option>
            <option value="Ms. Fatma Ibrahim">
              Ms. Fatma Ibrahim - Corporate Law
            </option>
            <option value="Mr. Omar Ali">
              Mr. Omar Ali - Regulatory Compliance
            </option>
          </select>
        </div>
        <Input
          label="Due Date"
          type="date"
          value={formData.dueDate}
          onChange={(e) =>
            setFormData({ ...formData, dueDate: e.target.value })
          }
          required
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Additional instructions or context..."
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Assign Workflow</Button>
        </div>
      </form>
    </Modal>
  );
}
